import { IonButton, IonContent, IonModal } from '@ionic/react';
import { useCallback, useEffect, useState } from 'react';

import bookingLinkImage from '@/assets/images/onboarding/booking-link.png';
import remindersImage from '@/assets/images/onboarding/reminders.png';
import requestsImage from '@/assets/images/onboarding/requests.png';
import {
  ButtonContainer,
  Padding,
  ProgressBar,
  Promo
} from '@/components/common';
import { Text } from '@/components/memo';
import { useOnboarding } from '@/contexts';

import './Onboarding.css';

const SLIDES = [
  {
    image: bookingLinkImage,
    header: (
      <>
        Make it <Text color="primary">easy</Text> for your patients
      </>
    ),
    subheader:
      'Get a booking link where patients can directly book appointments.'
  },
  {
    image: requestsImage,
    header: (
      <>
        Make it <Text color="primary">convenient</Text> for you
      </>
    ),
    subheader: 'Approve or decline appointment requests in one click.'
  },
  {
    image: remindersImage,
    header: (
      <>
        Let <Text color="primary">Odonto</Text> do the work
      </>
    ),
    subheader: 'We send reminders to your patients before each appointment.'
  }
];

interface OnboardingProps {
  isOpen: boolean;
  delay?: number;
}

const Onboarding: React.FC<OnboardingProps> = ({
  isOpen: isOpenProp,
  delay
}) => {
  const { onCompleteOnboarding } = useOnboarding();

  const [slide, setSlide] = useState<number>(0);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const isFirstSlide = slide === 0;
  const isLastSlide = slide === SLIDES.length - 1;

  const onNext = useCallback(() => {
    if (slide < SLIDES.length - 1) {
      setSlide((prev) => prev + 1);
    } else {
      onCompleteOnboarding();
      setIsOpen(false);
    }
  }, [slide, onCompleteOnboarding]);

  const onBack = useCallback(() => {
    if (slide > 0) {
      setSlide((prev) => prev - 1);
    }
  }, [slide]);

  const onSkip = useCallback(() => {
    onCompleteOnboarding();
    setIsOpen(false);
  }, [onCompleteOnboarding]);

  const copy = {
    back: 'Back',
    next: 'Next',
    skip: 'Skip',
    continue: "Let's go!"
  };

  /**
   * Delay the onboarding modal opening for smoother user experience
   */
  useEffect(() => {
    if (delay && isOpenProp) {
      setTimeout(() => setIsOpen(isOpenProp), delay);
    } else {
      setIsOpen(isOpenProp);
    }
  }, [isOpenProp, delay]);

  return (
    <IonModal isOpen={isOpen} id="onboarding">
      <IonContent scrollX={false} scrollY={false}>
        <Padding>
          <ProgressBar totalSteps={SLIDES.length} currentProgress={slide} />
          <div className="onboarding__promo-container">
            <Promo
              containerStyle={{ padding: '16px 0 0' }}
              image={SLIDES[slide].image}
              imageStyle={{
                maxWidth: 271,
                backgroundColor: 'var(--ion-color-accent)',
                borderRadius: 12,
                boxShadow: 'var(--shadow)'
              }}
              header={SLIDES[slide].header}
              subHeader={SLIDES[slide].subheader}
              disableImageBloop
              enableImageFloat
            />
          </div>
          <ButtonContainer>
            <IonButton
              id="next-button"
              onClick={onNext}
              expand="block"
              fill="solid"
              size="default"
              color="primary"
              strong={true}
            >
              {isLastSlide ? copy.continue : copy.next}
            </IonButton>
            {isFirstSlide ? (
              <IonButton
                id="skip-button"
                onClick={onSkip}
                expand="block"
                fill="clear"
                size="default"
                color="medium"
                strong={true}
              >
                {copy.skip}
              </IonButton>
            ) : (
              <IonButton
                id="back-button"
                onClick={onBack}
                expand="block"
                fill="clear"
                size="default"
                color="medium"
                strong={true}
              >
                {copy.back}
              </IonButton>
            )}
          </ButtonContainer>
        </Padding>
      </IonContent>
    </IonModal>
  );
};

export default Onboarding;
